import { patchState } from '@ngrx/signals';
import { AuthStore } from '../models/auth.models';
import { AuthService } from '@auth';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, distinctUntilChanged, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { LOG_AUTH_STORE_KEY } from './auth.constants';

/**
 * Attempts to log in the user by invoking the login method from the AuthService.
 * @param store - The AuthStore where the authentication state is managed.
 * @param authService - The AuthService that handles authentication logic.
 * @param target - The target string used for login (typically a URL or user identifier).
 *
 * Upon success, updates the store state to mark the user as authenticated.
 * If login fails, an error is logged to the console.
 */
export const login = (
  store: AuthStore,
  authService: AuthService,
  target: string,
) => {
  authService.login(target).subscribe({
    next: () => {
      // Mark the store as authenticated when login is successful.
      patchState(store, {
        isAuthenticated: true,
      });
    },
    error: (err) => console.error(`${LOG_AUTH_STORE_KEY} Login failed:`, err),
    // Log an error message if login fails.
  });
};

/**
 * Logs out the user by calling the logout method from AuthService.
 * @param store - The AuthStore managing authentication state.
 * @param authService - The AuthService that performs logout operations.
 *
 * Resets the authentication state upon successful logout, clearing user data and token.
 * Logs an error message if logout fails.
 */
export const logout = (store: AuthStore, authService: AuthService) => {
  authService.logout().subscribe({
    next: () => {
      // Clear all localStorage and sessionStorage on logout
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {
        console.warn('Error clearing storage on logout:', e);
      }
      // Reset authentication state upon successful logout.
      patchState(store, {
        isAuthenticated: false,
        user: null,
      });
    },
    error: (err) => console.error(`${LOG_AUTH_STORE_KEY} Logout failed:`, err),
  });
};

/**
 * Loads the current authentication state from the AuthService.
 * @param store - The AuthStore that holds the authentication state.
 * @param authService - The AuthService that retrieves the authenticated user.
 *
 * Sets the store to a loading state, makes a request to the AuthService for user data,
 * and updates the store based on the result.
 * If no user is found, redirects to the login page.
 */
export const loadAuthState = (store: AuthStore, authService: AuthService) => {
  return rxMethod<void>(
    pipe(
      debounceTime(100), // Debounce requests by 100ms to avoid redundant actions.
      distinctUntilChanged(), // Only proceed if the state has changed.
      tap(() => {
        // Set the store to loading state while fetching the authentication data.
        patchState(store, { isAuthenticated: false, isLoading: true });
      }),
      switchMap(() => {
        // Fetch the user state from AuthService.
        return authService.authUser$().pipe(
          tapResponse({
            next: (user) => {
              patchState(store, {
                user,
                isAuthenticated: !!user,
                isLoading: false,
              });
              if (!user) {
                console.warn(
                  `${LOG_AUTH_STORE_KEY} No user found. Redirecting to login.`,
                );
                authService.login('/callback');
              }
            },
            error: (error) => {
              console.error(
                `${LOG_AUTH_STORE_KEY} Error loading auth state:`,
                error,
              );
              patchState(store, { isLoading: false });
            },
          }),
        );
      }),
    ),
  );
};
